/**
 * ClinicCard — morning-glance card for a single clinic (V1.1 plan §5.7).
 *
 * Layout:
 *   - Clinic name + alias/TIN in the header
 *   - NEXT REVIEW: most-recent upcoming (scheduled) meeting, if any
 *   - RECENT REVIEWS: last 4 past meetings (held), each linking to the
 *     pinned-meeting URL
 *   - "Open current dashboard →" primary button (full-width)
 *
 * Data source: `useClinicMeetings(clinicId)` per card. This is an N+1
 * pattern by design (V1.1 plan §5.7 (a) — acceptable at dev-seed scale).
 * V1.2 may add a batch endpoint.
 *
 * Non-pinnable meetings (is_pinnable_snapshot_available=false) render as
 * static text without a [view →] link, with a small "(no snapshot)"
 * annotation. The backend's v0.3.1 guidance: don't link to meetings
 * that would produce an empty dashboard.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "@tensaw/design-system";
import type { ClinicSummary, MeetingListItem } from "@/api/models";
import { useClinicMeetings } from "@/hooks/useClinicMeetings";
import { formatDateTime, formatDate } from "@/lib/format";

export interface ClinicCardProps {
  clinic: ClinicSummary;
}

interface SortedMeetings {
  upcoming: MeetingListItem | null;
  recent: MeetingListItem[];
}

function sortMeetings(items: MeetingListItem[]): SortedMeetings {
  // Backend already sorts newest-first by COALESCE(held_started_at, scheduled_at).
  // We split into:
  //   - one "next upcoming" (scheduled, no held times)
  //   - up to 4 "recent" past (has held times)
  let upcoming: MeetingListItem | null = null;
  const recent: MeetingListItem[] = [];
  // Scan in chronological order to find the next upcoming (smallest scheduled_at > now).
  const now = new Date().getTime();
  const upcomingCandidates: MeetingListItem[] = [];
  for (const m of items) {
    if (m.held_started_at) {
      if (recent.length < 4) recent.push(m);
      continue;
    }
    // No held times → either scheduled or in_progress
    if (m.scheduled_at) {
      const t = new Date(m.scheduled_at).getTime();
      if (Number.isFinite(t) && t >= now) {
        upcomingCandidates.push(m);
      }
    }
  }
  // Soonest upcoming wins.
  upcomingCandidates.sort((a, b) => {
    const ta = new Date(a.scheduled_at!).getTime();
    const tb = new Date(b.scheduled_at!).getTime();
    return ta - tb;
  });
  upcoming = upcomingCandidates[0] ?? null;
  return { upcoming, recent };
}

function ReviewRow({
  meeting,
  clinicId,
}: {
  meeting: MeetingListItem;
  clinicId: number;
}) {
  const heldDate = meeting.held_started_at
    ? formatDate(meeting.held_started_at)
    : meeting.scheduled_at
      ? formatDate(meeting.scheduled_at)
      : "—";

  const url = meeting.is_pinnable_snapshot_available
    ? `/clinics/${clinicId}/year/${meeting.performance_year}/meeting/${meeting.meeting_id}`
    : null;

  const labelLine = (
    <>
      <span className="font-medium text-gray-800">
        Q{meeting.quarter} {meeting.performance_year}
      </span>
      <span className="text-gray-500"> · held {heldDate}</span>
      <span className="text-gray-500"> · AM {meeting.am_display_name}</span>
      {!meeting.is_pinnable_snapshot_available ? (
        <span className="ml-1 text-xs text-gray-400">(no snapshot)</span>
      ) : null}
    </>
  );

  return (
    <li className="flex items-center justify-between py-1 text-sm">
      <span>{labelLine}</span>
      {url ? (
        <Link
          to={url}
          className="text-xs font-medium text-tw-teal-700 hover:underline"
        >
          view →
        </Link>
      ) : null}
    </li>
  );
}

export function ClinicCard({ clinic }: ClinicCardProps) {
  const meetingsQ = useClinicMeetings(clinic.clinic_id);

  const sorted = useMemo(() => {
    if (!meetingsQ.data) return null;
    return sortMeetings(meetingsQ.data.meetings);
  }, [meetingsQ.data]);

  return (
    <div className="flex flex-col rounded-card border border-border bg-white shadow-card">
      <div className="border-b border-border px-4 py-3">
        <div className="text-base font-semibold text-gray-800">{clinic.name}</div>
        <div className="mt-0.5 text-xs text-gray-500">
          {clinic.alias ? <span>{clinic.alias} · </span> : null}
          <span className="tnum">TIN {clinic.tin}</span>
        </div>
      </div>

      <div className="flex-1 px-4 py-3">
        {meetingsQ.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
            <Spinner /> <span>Loading recent reviews…</span>
          </div>
        ) : meetingsQ.isError ? (
          <div className="py-2 text-xs text-red-600">
            Couldn&apos;t load recent reviews.
          </div>
        ) : sorted ? (
          <>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Next review
            </div>
            <div className="mt-1 text-sm">
              {sorted.upcoming ? (
                <>
                  <span className="font-medium text-gray-800">{sorted.upcoming.label}</span>
                  {sorted.upcoming.scheduled_at ? (
                    <span className="text-gray-500">
                      {" · scheduled "}
                      {formatDateTime(sorted.upcoming.scheduled_at)}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="italic text-gray-400">No upcoming review scheduled</span>
              )}
            </div>

            <div className="mt-3 text-xs font-medium uppercase tracking-wider text-gray-500">
              Recent reviews
            </div>
            {sorted.recent.length === 0 ? (
              <div className="mt-1 text-sm italic text-gray-400">No past reviews yet</div>
            ) : (
              <ul className="mt-1 divide-y divide-gray-100">
                {sorted.recent.map((m) => (
                  <ReviewRow key={m.meeting_id} meeting={m} clinicId={clinic.clinic_id} />
                ))}
              </ul>
            )}
          </>
        ) : null}
      </div>

      <div className="border-t border-border px-4 py-3">
        <Link
          to={`/clinics/${clinic.clinic_id}`}
          className="block w-full rounded-button bg-brand-primary px-3 py-1.5 text-center text-sm font-semibold text-white hover:bg-brand-hover"
        >
          Open current dashboard →
        </Link>
      </div>
    </div>
  );
}
